// API Service with environment variable check and caching
import axios from 'axios';
import { getIconUrl } from './iconService';

const API_URL = import.meta.env.VITE_API_URL;
const API_KEY = import.meta.env.VITE_API_KEY;

// Determine baseURL:
// - If VITE_API_URL is set, use it directly (production)
// - Otherwise use '/api' which gets proxied by Vite (development)
const baseURL = API_URL || '/api';

// Create axios instance
const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    ...(API_KEY && { 'X-API-Key': API_KEY }),
  },
  timeout: 30000,
  // Enable sending cookies/credentials with requests
  withCredentials: true,
});

// Add request interceptor to include JWT token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          // Call refresh token endpoint directly with axios to avoid interceptor loop
          const response = await axios.post(
            `${baseURL}/auth/refresh-token`,
            { refreshToken },
            { withCredentials: true }
          );
          
          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Cache storage
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCached = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setCached = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

// Dummy data generators
const generateDummyCurrencies = () => [
  { id: 'bitcoin', ticker: 'btc', name: 'Bitcoin', image: getIconUrl('bitcoin'), network: 'BTC', hasExtraId: false, precision: 8, isPopular: true },
  { id: 'ethereum', ticker: 'eth', name: 'Ethereum', image: getIconUrl('ethereum'), network: 'ETH', hasExtraId: false, precision: 18, isPopular: true },
  { id: 'tether', ticker: 'usdt', name: 'Tether (ERC20)', image: getIconUrl('tether'), network: 'ETH', hasExtraId: false, precision: 6, isPopular: true, isStable: true },
  { id: 'tether-trc20', ticker: 'usdt', name: 'Tether (TRC20)', image: getIconUrl('tether'), network: 'TRX', hasExtraId: false, precision: 6, isStable: true },
  { id: 'usdc', ticker: 'usdc', name: 'USD Coin', image: getIconUrl('usd-coin'), network: 'ETH', hasExtraId: false, precision: 6, isStable: true },
  { id: 'binancecoin', ticker: 'bnb', name: 'BNB', image: getIconUrl('binancecoin'), network: 'BSC', hasExtraId: false, precision: 18, isPopular: true },
  { id: 'solana', ticker: 'sol', name: 'Solana', image: getIconUrl('solana'), network: 'SOL', hasExtraId: false, precision: 9, isPopular: true },
  { id: 'cardano', ticker: 'ada', name: 'Cardano', image: getIconUrl('cardano'), network: 'ADA', hasExtraId: true, extraIdName: 'Memo', precision: 6, isPopular: true },
  { id: 'ripple', ticker: 'xrp', name: 'XRP', image: getIconUrl('ripple'), network: 'XRP', hasExtraId: true, extraIdName: 'Destination Tag', precision: 6, isPopular: true },
  { id: 'polkadot', ticker: 'dot', name: 'Polkadot', image: getIconUrl('polkadot'), network: 'DOT', hasExtraId: false, precision: 10, isPopular: true },
  { id: 'dogecoin', ticker: 'doge', name: 'Dogecoin', image: getIconUrl('dogecoin'), network: 'DOGE', hasExtraId: false, precision: 8, isPopular: true },
  { id: 'avalanche-2', ticker: 'avax', name: 'Avalanche', image: getIconUrl('avalanche-2'), network: 'AVAX', hasExtraId: false, precision: 18 },
  { id: 'polygon', ticker: 'matic', name: 'Polygon', image: getIconUrl('polygon'), network: 'MATIC', hasExtraId: false, precision: 18, isPopular: true },
  { id: 'litecoin', ticker: 'ltc', name: 'Litecoin', image: getIconUrl('litecoin'), network: 'LTC', hasExtraId: false, precision: 8 },
  { id: 'chainlink', ticker: 'link', name: 'Chainlink', image: getIconUrl('chainlink'), network: 'ETH', hasExtraId: false, precision: 18 },
  { id: 'uniswap', ticker: 'uni', name: 'Uniswap', image: getIconUrl('uniswap'), network: 'ETH', hasExtraId: false, precision: 18 },
  { id: 'cosmos', ticker: 'atom', name: 'Cosmos', image: getIconUrl('cosmos'), network: 'ATOM', hasExtraId: true, extraIdName: 'Memo', precision: 6 },
  { id: 'stellar', ticker: 'xlm', name: 'Stellar', image: getIconUrl('stellar'), network: 'XLM', hasExtraId: true, extraIdName: 'Memo', precision: 7 },
  { id: 'tron', ticker: 'trx', name: 'TRON', image: getIconUrl('tron'), network: 'TRX', hasExtraId: false, precision: 6 },
  { id: 'monero', ticker: 'xmr', name: 'Monero', image: getIconUrl('monero'), network: 'XMR', hasExtraId: false, precision: 12 },
  { id: 'algorand', ticker: 'algo', name: 'Algorand', image: getIconUrl('algorand'), network: 'ALGO', hasExtraId: false, precision: 6 },
  { id: 'dai', ticker: 'dai', name: 'Dai', image: getIconUrl('dai'), network: 'ETH', hasExtraId: false, precision: 18, isStable: true },
  { id: 'busd', ticker: 'busd', name: 'BUSD', image: getIconUrl('binance-usd'), network: 'BSC', hasExtraId: false, precision: 18, isStable: true },
  { id: 'trueusd', ticker: 'tusd', name: 'TrueUSD', image: getIconUrl('true-usd'), network: 'ETH', hasExtraId: false, precision: 18, isStable: true },
  { id: 'shiba-inu', ticker: 'shib', name: 'Shiba Inu', image: getIconUrl('shiba-inu'), network: 'ETH', hasExtraId: false, precision: 18 },
  { id: 'filecoin', ticker: 'fil', name: 'Filecoin', image: getIconUrl('filecoin'), network: 'FIL', hasExtraId: false, precision: 18 },
  { id: 'vechain', ticker: 'vet', name: 'VeChain', image: getIconUrl('vechain'), network: 'VET', hasExtraId: false, precision: 18 },
  { id: 'tezos', ticker: 'xtz', name: 'Tezos', image: getIconUrl('tezos'), network: 'XTZ', hasExtraId: true, extraIdName: 'Destination Tag', precision: 6 },
  { id: 'eos', ticker: 'eos', name: 'EOS', image: getIconUrl('eos'), network: 'EOS', hasExtraId: true, extraIdName: 'Memo', precision: 4 },
  { id: 'aave', ticker: 'aave', name: 'Aave', image: getIconUrl('aave'), network: 'ETH', hasExtraId: false, precision: 18 },
  { id: 'compound', ticker: 'comp', name: 'Compound', image: getIconUrl('compound'), network: 'ETH', hasExtraId: false, precision: 18 },
  { id: 'maker', ticker: 'mkr', name: 'Maker', image: getIconUrl('maker'), network: 'ETH', hasExtraId: false, precision: 18 },
  { id: 'synthetix', ticker: 'snx', name: 'Synthetix', image: getIconUrl('synthetix'), network: 'ETH', hasExtraId: false, precision: 18 },
  { id: 'the-graph', ticker: 'grt', name: 'The Graph', image: getIconUrl('the-graph'), network: 'ETH', hasExtraId: false, precision: 18 },
  { id: 'curve-dao', ticker: 'crv', name: 'Curve DAO', image: getIconUrl('curve-dao-token'), network: 'ETH', hasExtraId: false, precision: 18 },
  { id: '1inch', ticker: '1inch', name: '1inch', image: getIconUrl('1inch'), network: 'ETH', hasExtraId: false, precision: 18 },
  { id: 'loopring', ticker: 'lrc', name: 'Loopring', image: getIconUrl('loopring'), network: 'ETH', hasExtraId: false, precision: 18 },
  { id: 'zilliqa', ticker: 'zil', name: 'Zilliqa', image: getIconUrl('zilliqa'), network: 'ZIL', hasExtraId: false, precision: 12 },
  { id: 'neo', ticker: 'neo', name: 'NEO', image: getIconUrl('neo'), network: 'NEO', hasExtraId: false, precision: 0 },
  { id: 'qtum', ticker: 'qtum', name: 'Qtum', image: getIconUrl('qtum'), network: 'QTUM', hasExtraId: false, precision: 8 },
  { id: 'dash', ticker: 'dash', name: 'Dash', image: getIconUrl('dash'), network: 'DASH', hasExtraId: false, precision: 8 },
  { id: 'zcash', ticker: 'zec', name: 'Zcash', image: getIconUrl('zcash'), network: 'ZEC', hasExtraId: false, precision: 8 },
  { id: 'bitcoin-cash', ticker: 'bch', name: 'Bitcoin Cash', image: getIconUrl('bitcoin-cash'), network: 'BCH', hasExtraId: false, precision: 8 },
  { id: 'ethereum-classic', ticker: 'etc', name: 'Ethereum Classic', image: getIconUrl('ethereum-classic'), network: 'ETC', hasExtraId: false, precision: 18 },
];

const generateDummyExchangeAmount = (from, to, amount, arbitrage = false) => {
  const rates = {
    'btc-eth': 15.5,
    'eth-btc': 0.064,
    'btc-usdt': 43250,
    'usdt-btc': 0.000023,
    'eth-usdt': 2780,
    'usdt-eth': 0.000359,
    'sol-eth': 0.035,
    'eth-sol': 28.5,
    'bnb-eth': 0.113,
    'eth-bnb': 8.85,
    'ada-eth': 0.00021,
    'eth-ada': 4760,
    'xrp-eth': 0.00022,
    'eth-xrp': 4480,
    'dot-eth': 0.0028,
    'eth-dot': 355,
    'doge-eth': 0.000032,
    'eth-doge': 31250,
    'matic-eth': 0.00035,
    'eth-matic': 2850,
  };
  
  const key = `${from}-${to}`;
  const reverseKey = `${to}-${from}`;
  let rate = rates[key];
  
  if (!rate) {
    // Generate pseudo-random but consistent rate
    const seed = from.charCodeAt(0) + to.charCodeAt(0);
    rate = (seed % 1000) / 100 + 0.1;
  }
  
  // Apply arbitrage boost if enabled (10-15% better rate)
  if (arbitrage) {
    const boostPercent = 10 + Math.random() * 5;
    rate = rate * (1 + boostPercent / 100);
  }
  
  const estimatedAmount = (parseFloat(amount) * rate * 0.995).toFixed(8);
  
  return {
    estimatedAmount,
    rate,
    networkFee: (parseFloat(amount) * 0.001).toFixed(8),
    serviceFee: (parseFloat(amount) * 0.004).toFixed(8),
    validUntil: Date.now() + 60000,
    arbitrage,
  };
};

const generateDummyTransaction = (id, status = 'waiting') => ({
  id,
  status,
  fromCurrency: 'btc',
  toCurrency: 'eth',
  fromAmount: '0.5',
  toAmount: '7.725',
  depositAddress: 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh',
  depositExtraId: null,
  recipientAddress: '0x71C7656EC7ab88b098defB751B7401B5f6d8976F',
  recipientExtraId: null,
  createdAt: Date.now(),
  updatedAt: Date.now(),
  payinConfirmations: status === 'waiting' ? 0 : 2,
  payinRequiredConfirmations: 3,
  payoutHash: status === 'finished' ? '0x1234567890abcdef1234567890abcdef12345678' : null,
  payinHash: status !== 'waiting' ? 'abc123def456789abc123def456789abc123def456789' : null,
});

// API Functions
export const fetchCurrencies = async () => {
  const cacheKey = 'currencies';
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await apiClient.get('/exchange/currencies');
    setCached(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('API Error, using dummy data:', error);
    const data = generateDummyCurrencies();
    setCached(cacheKey, data);
    return data;
  }
};

export const fetchExchangeAmount = async (from, to, amount, arbitrage = false) => {
  const cacheKey = `rate-${from}-${to}-${amount}-${arbitrage ? '1' : '0'}`;
  const cached = getCached(cacheKey);
  if (cached && cached.validUntil > Date.now()) return cached;

  try {
    const response = await apiClient.post('/exchange/estimate', {
      fromCurrency: from,
      toCurrency: to,
      fromAmount: parseFloat(amount),
      arbitrage
    });
    const data = { 
      estimatedAmount: response.data.toAmount,
      rate: response.data.rate,
      networkFee: 'Included',
      serviceFee: (parseFloat(amount) * (response.data.fee / 100)).toFixed(8),
      validUntil: Date.now() + 300000, // 5 minutes
      arbitrage
    };
    setCached(cacheKey, data);
    return data;
  } catch (error) {
    console.error('API Error, using dummy data:', error);
    const data = generateDummyExchangeAmount(from, to, amount, arbitrage);
    setCached(cacheKey, data);
    return data;
  }
};

// Client-side address validation patterns
const ADDRESS_PATTERNS = {
  eth: /^0x[a-fA-F0-9]{40}$/,
  btc: /^(1|3|bc1)[a-zA-Z0-9]{25,62}$/,
  ltc: /^(L|M|ltc1)[a-zA-Z0-9]{25,62}$/,
  bnb: /^(bnb1|0x)[a-zA-Z0-9]{39,42}$/,
  doge: /^D[5-9A-HJ-NP-U][a-zA-Z0-9]{32}$/,
  xrp: /^r[a-zA-Z0-9]{24,34}$/,
  sol: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
  trx: /^T[a-zA-Z0-9]{33}$/,
  ada: /^(addr1|Ae2tdPwUPE)[a-zA-Z0-9]{58,}$/,
  xmr: /^[48][a-zA-Z0-9]{94}$/,
};

const validateAddressClientSide = (currency, address) => {
  const ticker = currency?.toLowerCase();
  const pattern = ADDRESS_PATTERNS[ticker];
  
  if (!pattern) {
    // No pattern available, return null to indicate we should try API
    return null;
  }
  
  const isValid = pattern.test(address);
  return {
    isValid,
    message: isValid ? null : `Invalid ${ticker?.toUpperCase()} address format`,
  };
};

export const validateAddress = async (currency, address, extraId = null) => {
  if (!address || address.trim() === '') {
    return { isValid: false, message: 'Address is required' };
  }

  // Try client-side validation first
  const clientValidation = validateAddressClientSide(currency, address.trim());
  
  // If we have a client-side validation result, use it
  if (clientValidation !== null) {
    return clientValidation;
  }

  // Fall back to API for currencies we don't have patterns for
  try {
    const response = await apiClient.post('/exchange/validate-address', {
      currency,
      address,
      extraId,
    });
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    // If API fails, do basic length check as fallback
    const isValid = address && address.length > 10;
    return { isValid, message: isValid ? null : 'Invalid address format' };
  }
};

// Guest ID management
export const getGuestId = () => {
  let guestId = localStorage.getItem('guestId');
  if (!guestId) {
    guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('guestId', guestId);
  }
  return guestId;
};

export const clearGuestId = () => {
  localStorage.removeItem('guestId');
};

export const createTransaction = async (params) => {
  try {
    // Add guestId if user is not authenticated
    const token = localStorage.getItem('accessToken');
    const payload = token ? { ...params } : { ...params, guestId: getGuestId() };
    
    const response = await apiClient.post('/transactions', payload);
    
    // Also store in localStorage for quick access
    const stored = JSON.parse(localStorage.getItem('transactionHistory') || '[]');
    stored.unshift({
      ...response.data,
      createdAt: new Date().toISOString(),
    });
    localStorage.setItem('transactionHistory', JSON.stringify(stored.slice(0, 50)));
    
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to create transaction');
  }
};

export const fetchTransaction = async (id) => {
  try {
    const response = await apiClient.get(`/transactions/${id}`);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch transaction');
  }
};

// Fetch transactions for current user or guest
export const fetchTransactions = async (options = {}) => {
  try {
    const token = localStorage.getItem('accessToken');
    const params = token ? options : { ...options, guestId: getGuestId() };
    
    const response = await apiClient.get('/transactions', { params });
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    // Fallback to localStorage for guests if server fails
    if (!localStorage.getItem('accessToken')) {
      const stored = JSON.parse(localStorage.getItem('transactionHistory') || '[]');
      return { transactions: stored, total: stored.length };
    }
    throw new Error(error.response?.data?.message || 'Failed to fetch transactions');
  }
};

export const fetchEstimatedArrivalTime = async (from, to, arbitrage = false) => {
  const cacheKey = `eta-${from}-${to}-${arbitrage ? '1' : '0'}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await apiClient.get('/exchange/rate', {
      params: { from, to, arbitrage: arbitrage ? '1' : '0' },
    });
    const data = { estimatedTime: response.data.estimatedTime };
    setCached(cacheKey, data);
    return data;
  } catch (error) {
    console.error('API Error:', error);
    return { estimatedTime: 15 };
  }
};

export const fetchMarketData = async () => {
  const cacheKey = 'market-data';
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await apiClient.get('/exchange/market-data');
    setCached(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    return {};
  }
};

// Auth API Functions
export const registerUser = async (userData) => {
  try {
    // Include guestId for transaction migration
    const guestId = getGuestId();
    const response = await apiClient.post('/auth/register', { ...userData, guestId });
    
    // Clear guestId after successful registration with migration
    clearGuestId();
    
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.error || 'Registration failed');
  }
};

export const loginUser = async (credentials) => {
  try {
    // Include guestId for transaction migration
    const guestId = getGuestId();
    const response = await apiClient.post('/auth/login', { ...credentials, guestId });
    
    // Store tokens
    localStorage.setItem('accessToken', response.data.accessToken);
    localStorage.setItem('refreshToken', response.data.refreshToken);
    
    // Clear guestId after successful login with migration
    clearGuestId();
    
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.error || 'Login failed');
  }
};

export const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await apiClient.post('/auth/refresh-token', { refreshToken });
    localStorage.setItem('accessToken', response.data.accessToken);
    localStorage.setItem('refreshToken', response.data.refreshToken);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    // Clear tokens on refresh failure
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    throw new Error('Session expired. Please login again.');
  }
};

export const logoutUser = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
};

// Get stored token
export const getAccessToken = () => localStorage.getItem('accessToken');

// Fetch live prices for all trending coins
export const fetchLivePrices = async () => {
  const cacheKey = 'live-prices';
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await apiClient.get('/exchange/prices');
    setCached(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    return { coins: [], lastUpdated: Date.now() };
  }
};

// Fetch price for a specific token
export const fetchTokenPrice = async (coinId) => {
  const cacheKey = `token-price-${coinId}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await apiClient.get(`/exchange/prices/${coinId}`);
    setCached(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    return null;
  }
};

export default apiClient;
